import { useState } from 'react';
import { ussd } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Text, Button, Badge } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { TenantName } from '../../../components/TenantName';
import { formatDateTime, formatMinor } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useUssdApplications, useApproveUssdApplication, useRejectUssdApplication } from '../../../data/useUssd';
import { useT } from '../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';
const STATUS_COLOUR: Record<ussd.UssdApplicationStatus, string> = { submitted: 'yellow', approved: 'green', rejected: 'red', withdrawn: 'gray' };

/**
 * Who has asked for a code, those waiting first. Approving needs the code to
 * give: an extension's digits on the shared code, or a dedicated code as it
 * is dialled. It takes the held set-up fee and charges the rest of the month
 * in one step; rejecting needs a reason the business is shown, and gives the
 * fee back.
 */
export function ApplicationsCard({ sharedCode }: { sharedCode: string }) {
  const t = useT();
  const applications = useUssdApplications();
  const approve = useApproveUssdApplication();
  const reject = useRejectUssdApplication();
  const [assign, setAssign] = useState<Record<string, string>>({});
  const [rejecting, setRejecting] = useState<ussd.UssdApplication | null>(null);
  const [reason, setReason] = useState('');
  const rows = (applications.data?.data ?? []) as Indexed<ussd.UssdApplication>[];

  const doApprove = async (a: ussd.UssdApplication) => {
    const value = (assign[a.id] ?? a.preferred_digits ?? '').trim();
    if (!value) return;
    try {
      const res = await approve.mutateAsync({ id: a.id, req: a.kind === 'extension' ? { extension: value } : { code: value } });
      notifySuccess(t(res.first_rental && res.first_rental !== 'paid' ? 'ussd.applications.approvedUnpaid' : 'ussd.applications.approved', { code: res.data.code ?? value }));
    } catch (e) { notifyError(e instanceof Error ? e.message : t('ussd.toast.failed')); }
  };
  const doReject = async () => {
    if (!rejecting) return;
    try {
      await reject.mutateAsync({ id: rejecting.id, reason: reason.trim() });
      notifySuccess(t('ussd.applications.rejected'));
      setRejecting(null); setReason('');
    } catch (e) { notifyError(e instanceof Error ? e.message : t('ussd.toast.failed')); }
  };

  return (
    <Card withBorder data-testid="ussd-applications">
      <Stack gap="md">
        <Stack gap="xs">
          <Text role="label">{t('ussd.applications.title')}</Text>
          <Text role="bodySmall" c={MUTED}>{t('ussd.applications.hint')}</Text>
        </Stack>
        {applications.isError && (
          <Alert color="error" role="alert">
            <Group justify="space-between" align="center" wrap="wrap">
              <Text role="bodySmall">{t('ussd.loadFailed')}</Text>
              <Button variant="outline" size="xs" onClick={() => void applications.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}
        {!applications.isPending && !applications.isError && rows.length === 0 && (
          <EmptyState title={t('ussd.applications.empty')} description={t('ussd.applications.emptyHint')} />
        )}
        {(applications.isPending || rows.length > 0) && (
          <DataTable
            records={rows}
            idAccessor="id"
            loading={applications.isPending}
            columns={[
              {
                accessor: 'organisation_id', title: t('ussd.col.organisation'),
                render: (r) => (
                  <Stack gap="xs">
                    <TenantName id={r.organisation_id} />
                    {r.submitted_by && <Text role="caption" c={MUTED}>{r.submitted_by}</Text>}
                  </Stack>
                )
              },
              {
                accessor: 'purpose', title: t('ussd.applications.purpose'),
                render: (r) => (
                  <Stack gap="xs">
                    <Text role="bodySmall">{t(`ussd.kind.${r.kind}`)}</Text>
                    <Text role="caption" c={MUTED} style={{ whiteSpace: 'pre-line' }}>{r.purpose}</Text>
                    {r.preferred_digits && <Text role="caption">{t('ussd.applications.preferred', { digits: r.preferred_digits })}</Text>}
                  </Stack>
                )
              },
              {
                accessor: 'fee_minor', title: t('ussd.applications.fees'),
                render: (r) => (
                  <Stack gap="xs">
                    <Text role="bodySmall">{t('ussd.applications.setup', { amount: formatMinor(r.fee_minor, 'ZAR') })}</Text>
                    <Text role="caption" c={MUTED}>{t('ussd.applications.monthly', { amount: formatMinor(r.monthly_minor, 'ZAR') })}</Text>
                    {r.fee_held && <Badge size="xs" variant="light" color="blue">{t('ussd.applications.held')}</Badge>}
                  </Stack>
                )
              },
              {
                accessor: 'status', title: t('ussd.col.status'),
                render: (r) => (
                  <Stack gap="xs">
                    <Badge variant="light" color={STATUS_COLOUR[r.status]}>{t(`ussd.applications.status.${r.status}`)}</Badge>
                    {r.code && <Text role="caption">{r.code}</Text>}
                    {r.reject_reason && <Text role="caption" c={MUTED}>{r.reject_reason}</Text>}
                    <Text role="caption" c={MUTED}>{formatDateTime(r.created_at)}</Text>
                  </Stack>
                )
              },
              {
                accessor: 'actions', title: t('common.actions'),
                render: (r) => (r.status !== 'submitted' ? null : (
                  <Stack gap="xs" style={{ minWidth: 200 }}>
                    <TextInput
                      size="xs"
                      aria-label={t(r.kind === 'extension' ? 'ussd.applications.extension' : 'ussd.applications.code')}
                      placeholder={r.kind === 'extension' ? t('ussd.applications.extensionPlaceholder', { shared: sharedCode || '*120*1234#' }) : '*120*5678#'}
                      value={assign[r.id] ?? r.preferred_digits ?? ''}
                      onChange={(e) => { const v = e.currentTarget.value; setAssign((m) => ({ ...m, [r.id]: v })); }}
                      description={t(r.kind === 'extension' ? 'ussd.applications.extensionHint' : 'ussd.applications.codeHint')}
                    />
                    <Group gap="sm" wrap="nowrap">
                      <Button
                        size="xs"
                        disabled={!(assign[r.id] ?? r.preferred_digits ?? '').trim()}
                        loading={approve.isPending && approve.variables?.id === r.id}
                        onClick={() => void doApprove(r)}
                      >
                        {t('ussd.applications.approve')}
                      </Button>
                      <Button size="xs" variant="default" color="red" onClick={() => setRejecting(r)}>{t('ussd.applications.reject')}</Button>
                    </Group>
                  </Stack>
                ))
              }
            ]}
          />
        )}
      </Stack>
      <Modal opened={rejecting !== null} onClose={() => { setRejecting(null); setReason(''); }} title={t('ussd.applications.rejectTitle')}>
        <Stack gap="md">
          <Text role="bodySmall">{t('ussd.applications.rejectBody')}</Text>
          <Textarea label={t('ussd.applications.reason')} description={t('ussd.applications.reasonHint')} value={reason} minRows={2} maxLength={500} onChange={(e) => setReason(e.currentTarget.value)} />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => { setRejecting(null); setReason(''); }}>{t('action.cancel')}</Button>
            <Button color="red" loading={reject.isPending} disabled={!reason.trim()} onClick={() => void doReject()}>{t('ussd.applications.reject')}</Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
